import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";
import { MARK, markOf } from "@/payroll/editor/roster-marks";
import type { EnrichedRosterMember, RosterField } from "@/payroll/editor/types";
import { TeamsCol } from "./cols";
import { RosterCell } from "./roster-cell";
// A changed value carries its own mark, so an updated row needs no tint of its
// own — but it still needs a solid colour under the pinned columns.
const TINT: Record<string, string> = {
  added: "bg-green-5",
  removed: "bg-red-5",
  updated: "bg-surface-primary",
};

// The gutter and the name stay put while the columns scroll under them: a Yes
// in the eighth column means nothing without the person it belongs to. They
// carry their own background, or the scrolled cells show through.
export const PINNED = "sticky z-10";

export const HEAD =
  "sticky top-0 z-10 border-b border-stroke-tertiary bg-surface-primary py-1.5 font-medium";

// Marks where the pinned columns end, so the half-scrolled column beside them
// reads as scrolled rather than as broken text.
export const EDGE = "shadow-[inset_-1px_0_0_0_hsl(var(--stroke-tertiary))]";

// Where cycling just sent the reader. The design system's own selected-row
// colour, and it has to go on the pinned cells too or the name and the gutter
// keep their solid background and the highlight stops halfway across the row.

export const RosterRow = ({
  member,
  fields,
  mark,
  here,
  found,
  hit,
}: {
  member: EnrichedRosterMember;
  fields: RosterField[];
  mark?: number;
  here: boolean;
  found: boolean;
  hit: boolean;
}) => {
  const kind = markOf(member);

  const ground = cn(TINT[member.change ?? ""] ?? "bg-surface-primary", {
    "bg-blue-5": here || found,
    "bg-blue-10": hit,
  });
  return (
    <tr
      data-member={member.id}
      data-change={mark}
      className={cn("border-b border-stroke-tertiary/60", ground)}
    >
      <td
        className={cn(
          PINNED,
          // The same `w-6` the header declares: the column collapsed to
          // its 6px dot, and the name pinned at `left-6` left a gap for
          // the scrolled columns to show through.
          // `align-middle` rather than a guessed top margin: the dot
          // sat high because `mt-2` is 8px into a row that is taller
          // than 16, and it would drift again the moment a row wrapped.
          "left-0 w-6 min-w-6 align-middle",
          ground,
        )}
      >
        {!!kind && (
          <>
            {/* The same sentence the legend carries, on the dot
                          itself, for anyone who scrolled past the header to get
                          here.

                          A change that was already waiting keeps its place in
                          the gutter but not the ink: it still goes live when
                          this publishes, and it is still not what was asked
                          for. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={cn(
                    "mx-auto block h-1.5 w-1.5 rounded-full",
                    MARK[kind].dot,
                  )}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {MARK[kind].why}
              </TooltipContent>
            </Tooltip>
            <span className="sr-only">
              {member.change === "removed"
                ? "Leaving the roster"
                : member.change === "added"
                  ? "Joining the roster"
                  : "Changed"}
              {". "}
              {MARK[kind].why}
            </span>
          </>
        )}
      </td>
      <td
        className={cn(PINNED, EDGE, "left-6 py-1 pr-3", ground, {
          "line-through": member.change === "removed",
        })}
      >
        {member.display_name}
      </td>
      <TeamsCol member={member} />
      {fields.map((field) => (
        <td key={field.key} className="py-1 pr-3">
          <RosterCell member={member} field={field} />
        </td>
      ))}
    </tr>
  );
};
